import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  createWorkOrderRepair,
  fetchWorkOrderRepairCorrections,
  updateWorkOrderRepair,
} from '#/api/momMgmt/workOrderRepair';
import { Button } from '#/modules/commonComponents/button';
import { AppDialog } from '#/modules/commonComponents/dialog';
import { DialogFooter } from '#/modules/commonComponents/dialog/DialogPrimitives';
import { dialogWideMaxWidthClassName } from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import WorkOrderRepairMaterialsEditor from '#/modules/momMgmt/workOrder/components/WorkOrderRepairMaterialsEditor';
import {
  WORK_ORDER_REPAIR_STATUS,
  isWorkOrderRepairQtyBalanced,
  getWorkOrderRepairErrorMessage,
  mapWorkOrderRepairToFormValues,
} from '#/modules/momMgmt/workOrder/helper/workOrderRepair';
import {
  WORK_ORDER_REPAIR_FIELD_LABELS,
  resolveWorkOrderRepairFieldLabel,
  workOrderRepairFormSchema,
} from '#/modules/momMgmt/workOrder/schemas/workOrderRepairSchema';
import { formatDateTimeOrDash } from '#/utils/date';
import { toast } from '#/utils/toast';

function CorrectionHistory({ repairId }) {
  const query = useQuery({
    queryKey: ['erp', 'workOrderRepair', 'corrections', repairId],
    queryFn: () => fetchWorkOrderRepairCorrections(repairId),
    enabled: Boolean(repairId),
  });
  const corrections = query.data?.data ?? [];
  if (corrections.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-col gap-2">
      <h3 className="form-block__title">更正紀錄</h3>
      <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm">
        {corrections.map((correction) => (
          <li key={correction.id} className="flex flex-wrap gap-x-3 text-gray-800">
            <span className="font-mono">{correction.correctionNo}</span>
            <span>{formatDateTimeOrDash(correction.record?.createdAt)}</span>
            <span>沖回 {correction.lines?.length ?? 0} 筆用料</span>
            <span className="text-muted-foreground">理由：{correction.reason}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

CorrectionHistory.propTypes = {
  repairId: PropTypes.string,
};

/**
 * 工單維修單建立／編輯（OPEN）／檢視（CLOSED）。
 * 未結時數量可暫不平衡；結單另在列表操作。
 */
const WorkOrderRepairDialog = ({
  open,
  onOpenChange,
  workOrderId,
  repair = null,
  canEdit = false,
  onSaved,
}) => {
  const isCreate = !repair;
  const isOpenRepair = repair?.status === WORK_ORDER_REPAIR_STATUS.OPEN;
  const readOnly = !isCreate && (!isOpenRepair || !canEdit);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitted },
  } = useForm({
    resolver: zodResolver(workOrderRepairFormSchema),
    defaultValues: mapWorkOrderRepairToFormValues(repair),
  });

  useEffect(() => {
    if (open) {
      reset(mapWorkOrderRepairToFormValues(repair));
    }
  }, [open, repair, reset]);

  const saveMutation = useMutation({
    mutationFn: (values) => (isCreate
      ? createWorkOrderRepair({ ...values, workOrderId })
      : updateWorkOrderRepair(repair.id, {
        ...values,
        updatedAt: repair.record?.updatedAt,
        revision: repair.revision,
      })),
    onSuccess: (result) => {
      toast.success(isCreate ? '已建立維修單' : '已更新維修單');
      onSaved?.(result?.data);
      onOpenChange(false);
    },
    onError: (error) => toast.error(getWorkOrderRepairErrorMessage(error, '儲存維修單失敗')),
  });

  const [problemQty, repairedQty, notRepairedQty] = useWatch({
    control,
    name: ['problemQty', 'repairedQty', 'notRepairedQty'],
  });
  const balanced = isWorkOrderRepairQtyBalanced({ problemQty, repairedQty, notRepairedQty });

  let title = '新增維修單';
  if (!isCreate) {
    title = readOnly ? `維修單 ${repair.repairNo ?? ''}` : `編輯維修單 ${repair.repairNo ?? ''}`;
  }

  const footer = (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
        {readOnly ? '關閉' : '取消'}
      </Button>
      {!readOnly ? (
        <Button
          type="button"
          disabled={saveMutation.isPending}
          onClick={handleSubmit((formValues) => saveMutation.mutate(formValues))}
        >
          {saveMutation.isPending ? '儲存中…' : '儲存'}
        </Button>
      ) : null}
    </DialogFooter>
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description="不維修數量只記錄本單決定不修的數量，不會改動工單報廢量。"
      maxWidthClassName={dialogWideMaxWidthClassName}
      footer={footer}
    >
      {/* pt-2：浮動 label 在捲動區頂端不被裁切 */}
      <div className="flex flex-col gap-4 pt-2">
        <FormValidationSummary
          show={isSubmitted}
          errors={errors}
          fieldLabels={WORK_ORDER_REPAIR_FIELD_LABELS}
          fieldLabelResolver={resolveWorkOrderRepairFieldLabel}
        />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <FloatingInput
            id="problemQty"
            label="問題數量"
            type="number"
            register={register}
            errors={errors}
            readOnly={readOnly}
          />
          <FloatingInput
            id="repairedQty"
            label="已修復數量"
            type="number"
            register={register}
            errors={errors}
            readOnly={readOnly}
          />
          <FloatingInput
            id="notRepairedQty"
            label="不維修數量"
            type="number"
            register={register}
            errors={errors}
            readOnly={readOnly}
          />
        </div>
        {!readOnly && !balanced ? (
          <p className="text-xs text-amber-700">
            目前問題數量 ≠ 已修復＋不維修；可先儲存，結單前須相等。
          </p>
        ) : null}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <FloatingTextarea
            id="problemDescription"
            label="問題描述"
            register={register}
            errors={errors}
            rows={3}
            readOnly={readOnly}
          />
          <FloatingTextarea
            id="handlingDescription"
            label="處理描述"
            register={register}
            errors={errors}
            rows={3}
            readOnly={readOnly}
          />
        </div>
        <FloatingTextarea
          id="remark"
          label="備註"
          register={register}
          errors={errors}
          rows={2}
          readOnly={readOnly}
        />
        <WorkOrderRepairMaterialsEditor
          control={control}
          register={register}
          errors={errors}
          setValue={setValue}
          readOnly={readOnly}
        />
        {repair?.status === WORK_ORDER_REPAIR_STATUS.CLOSED ? (
          <>
            <p className="text-sm text-slate-700">
              結單時間：{formatDateTimeOrDash(repair.closedAt)}
            </p>
            <CorrectionHistory repairId={repair.id} />
          </>
        ) : null}
      </div>
    </AppDialog>
  );
};

WorkOrderRepairDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  workOrderId: PropTypes.string,
  repair: PropTypes.object,
  canEdit: PropTypes.bool,
  onSaved: PropTypes.func,
};

export default WorkOrderRepairDialog;
